import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  ButtonBase,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { EXTERNAL_URLS, openExternalUrl } from '../../constants/urls';
import { primitiveColors, semanticColors, BORDER_RADIUS } from '@/theme/tokens';

interface ReferVoteMateDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * "Refer VoteMate" share dialog: copy the site link, or share it on a social
 * channel. Rendered outside whichever menu opened it (the left drawer or the
 * header account menu) so it clears that menu's overlay and focus trap.
 */
const ReferVoteMateDialog = ({ open, onClose }: ReferVoteMateDialogProps) => {
  const { t } = useTranslation('common');
  const [copied, setCopied] = useState(false);
  const urlRef = useRef<HTMLElement>(null);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(EXTERNAL_URLS.VOTEMATE_WEBSITE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // No clipboard access (insecure context or a permissions policy). Select
      // the visible link so a single keystroke copies it, instead of the
      // deprecated execCommand path; the button stays on "Copy" so nothing
      // claims a copy that did not happen.
      const el = urlRef.current;
      if (el) window.getSelection()?.selectAllChildren(el);
    }
  };

  const shareChannels = [
    {
      label: 'X / Twitter',
      color: '#000000',
      initial: '𝕏',
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(t('shareMessage'))}&url=${encodeURIComponent(EXTERNAL_URLS.VOTEMATE_WEBSITE)}`,
    },
    {
      label: 'Facebook',
      color: '#1877F2',
      initial: 'f',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(EXTERNAL_URLS.VOTEMATE_WEBSITE)}`,
    },
    {
      label: 'LinkedIn',
      color: '#0A66C2',
      initial: 'in',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(EXTERNAL_URLS.VOTEMATE_WEBSITE)}`,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="share-dialog-title"
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: BORDER_RADIUS,
          px: '8px',
          py: '8px',
          backgroundColor: semanticColors.surface,
        },
      }}
    >
      <DialogTitle
        id="share-dialog-title"
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
      >
        <Typography sx={{ fontSize: '16px', fontWeight: 600, color: semanticColors.textBody }}>
          {t('referVoteMate')}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label={t('closeShareDialog')}
          sx={{
            color: primitiveColors.gray22,
            '&:hover': { backgroundColor: semanticColors.overlaySubtle },
            '&:focus-visible': {
              outline: `2px solid ${semanticColors.brandPrimary}`,
              outlineOffset: '2px',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {/* Copy link row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '20px' }}>
          <Box
            sx={{
              flex: 1,
              border: `1px solid ${semanticColors.borderLight}`,
              borderRadius: BORDER_RADIUS,
              px: '10px',
              py: '7px',
              overflow: 'hidden',
            }}
          >
            <Typography
              ref={urlRef}
              sx={{
                fontSize: '12px',
                color: semanticColors.textTertiary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {EXTERNAL_URLS.VOTEMATE_WEBSITE}
            </Typography>
          </Box>
          <Tooltip title={copied ? t('copied') : t('copyLink')}>
            <ButtonBase
              onClick={handleCopyLink}
              aria-label={t('copyReferralLink')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                px: '12px',
                py: '8px',
                borderRadius: BORDER_RADIUS,
                backgroundColor: copied ? semanticColors.successAction : primitiveColors.blue7,
                color: semanticColors.textOnPrimary,
                fontSize: '12px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                '&:hover': { opacity: 0.9 },
                '&:focus-visible': {
                  outline: `2px solid ${semanticColors.brandPrimary}`,
                  outlineOffset: '2px',
                },
              }}
            >
              <ContentCopyIcon sx={{ fontSize: '14px' }} />
              {copied ? t('copied') : t('copy')}
            </ButtonBase>
          </Tooltip>
        </Box>

        {/* Social share buttons */}
        <Typography sx={{ fontSize: '12px', color: semanticColors.textTertiary, mb: '12px' }}>
          {t('shareVia')}
        </Typography>
        <Box sx={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
          {shareChannels.map(({ label, color, initial, url }) => (
            <Box
              key={label}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <ButtonBase
                onClick={() => openExternalUrl(url)}
                aria-label={t('shareOnPlatform', { platform: label })}
                sx={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: color,
                  color: semanticColors.textOnPrimary,
                  fontSize: '16px',
                  fontWeight: 700,
                  '&:hover': { opacity: 0.85 },
                  '&:focus-visible': {
                    outline: `2px solid ${semanticColors.brandPrimary}`,
                    outlineOffset: '2px',
                  },
                }}
              >
                {initial}
              </ButtonBase>
              <Typography sx={{ fontSize: '10px', color: semanticColors.textTertiary }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ReferVoteMateDialog;
